import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnChanges,
  Output,
} from '@angular/core';
import { addMonths, addWeeks, formatISO, parseISO, startOfMonth } from 'date-fns';
import type { FirestoreTimer, FirestoreWork, IsoDate } from '@jessie/commons';
import { NgIconWrapperComponent } from '../../../../../components/icons/ng-icon-wrapper/ng-icon-wrapper.component';
import { MonthCalendarComponent } from '../month-calendar/month-calendar.component';
import { RouterOutlet } from '@angular/router';
import {
  type CalendarTimeEntryChange,
  WeekCalendarComponent,
} from '../week-calendar/week-calendar.component';
import { calendarStatusLaneClasses, type CalendarStatusMessage } from './calendar-status-message';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styles: ['@reference "tailwindcss"; :host { @apply relative}'],
  imports: [MonthCalendarComponent, NgIconWrapperComponent, RouterOutlet, WeekCalendarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent implements OnChanges {
  @Input({ required: true }) work: Map<IsoDate, number> = new Map();
  @Input({ required: true }) workItems: FirestoreWork[] = [];
  @Input() timerItems: FirestoreTimer[] = [];
  @Input({ required: true }) selectedDate: IsoDate = formatISO(new Date(), {
    representation: 'date',
  });
  @Input({ required: true }) selectedMonth: IsoDate = formatISO(new Date(), {
    representation: 'date',
  });
  @Input() warningDays: IsoDate[] = [];
  @Input() weekStatusMessage: CalendarStatusMessage | null = null;
  @Input() pendingTimeEntryChange: CalendarTimeEntryChange | null = null;

  @Output() selectedDateChange = new EventEmitter<IsoDate>();
  @Output() selectedMonthChange = new EventEmitter<IsoDate>();
  @Output() createWork = new EventEmitter<{
    date: IsoDate;
    workMinutes: number;
    startTime: string;
  }>();
  @Output() editWork = new EventEmitter<{ work: FirestoreWork; sessionEntryId?: string }>();
  @Output() showTimer = new EventEmitter<{ timerId: string; date: IsoDate }>();
  @Output() timeEntryChange = new EventEmitter<CalendarTimeEntryChange>();

  months: IsoDate[] = [];
  viewMode: 'month' | 'week' = 'month';
  readonly monthStatusLaneClasses = calendarStatusLaneClasses;

  ngOnChanges(): void {
    this.months = [
      formatISO(addMonths(startOfMonth(parseISO(this.selectedMonth)), -2), {
        representation: 'date',
      }),
      formatISO(addMonths(startOfMonth(parseISO(this.selectedMonth)), -1), {
        representation: 'date',
      }),
      formatISO(startOfMonth(parseISO(this.selectedMonth)), { representation: 'date' }),
    ];
  }

  next() {
    if (this.viewMode === 'week') {
      this.selectDate(
        formatISO(addWeeks(parseISO(this.selectedDate), 1), { representation: 'date' })
      );
    } else {
      this.selectedMonthChange.emit(
        formatISO(startOfMonth(addMonths(parseISO(this.selectedMonth), 1)), {
          representation: 'date',
        })
      );
    }
  }

  previous() {
    if (this.viewMode === 'week') {
      this.selectDate(
        formatISO(addWeeks(parseISO(this.selectedDate), -1), { representation: 'date' })
      );
    } else {
      this.selectedMonthChange.emit(
        formatISO(startOfMonth(addMonths(parseISO(this.selectedMonth), -1)), {
          representation: 'date',
        })
      );
    }
  }

  selectDate(date: IsoDate): void {
    this.selectedDateChange.emit(date);
    this.selectedMonthChange.emit(
      formatISO(startOfMonth(parseISO(date)), { representation: 'date' })
    );
  }
}
